import React, { FC, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import {
	Platform,
	DeviceType,
	Orientation,
	WriteDirection,
	ArmrestsByCover,
	CoverItem,
	PopOverSceneItemData,
} from 'sofa-reborn-react';
import { ProductData, Product } from '@ikea-aoa/component-view-3d';
import { setSelectedArmrest } from '../store/armrest';
import ArmrestPopoverButton from '../components/ArmrestPopoverButton';
import { selectSceneArticle, selectSceneItem } from '../store/scene';
import { ProductUtils } from '../util/ProductUtils';

const ArmrestPopoverButtonContainer: FC = () => {
	const platform = useSelector<RootState, Platform>((state) => state.deviceInfo.platform);
	const deviceType = useSelector<RootState, DeviceType>((state) => state.deviceInfo.deviceType);
	const orientation = useSelector<RootState, Orientation>((state) => state.deviceInfo.orientation);
	const writeDirection = useSelector<RootState, WriteDirection>(
		(state) => state.localization.writeDirection
	);
	const productData = useSelector<RootState, ProductData>((state) => state.products.products);
	const selectedArmrest = useSelector<RootState, Product | null>(
		(state) => state.armrest.selectedArmrest
	);
	const coverName = useSelector<RootState, string | undefined>(
		(state) => state.cover.selectedCover?.coverName
	);
	const armrestsByCover = useSelector<RootState, ArmrestsByCover | null>(
		(state) => state.armrest.armrests
	);
	const selectedSceneItem = useSelector<RootState, PopOverSceneItemData>(
		(state) => state.scene.selectedSceneItem as PopOverSceneItemData
	);
	const selectedCover = useSelector<RootState, CoverItem | null>(
		(state) => state.cover.selectedCover
	);
	const coverOnly = useSelector<RootState, boolean | undefined>((state) => state.cover.coverOnly);
	const showInfoButton = selectedSceneItem
		? ProductUtils.getSprIdValid(productData, selectedSceneItem.articleId) || coverOnly
		: false;
	const dispatch = useDispatch();
	const selectedItemFamily = productData[selectedSceneItem.articleId].filter.family;
	const armrests = armrestsByCover && coverName ? armrestsByCover[coverName] : null;
	const hasArmrestPicker = armrestsByCover ? true : false;
	const wideArmrest = armrests?.wideArmrest ? true : false;

	const onClickChangeArmrest = () => {
		if (coverName && armrestsByCover) {
			const armrests = armrestsByCover[coverName];

			if (
				selectedArmrest === null ||
				(selectedArmrest == armrests?.regularArmrest && armrests.wideArmrest)
			) {
				dispatch(setSelectedArmrest(armrests.wideArmrest, true));
				dispatch(selectSceneArticle(armrests.wideArmrest.productId));
				const newSceneItem = {
					id: selectedSceneItem.id,
					articleId: armrests.wideArmrest.productId,
					screenPosition: selectedSceneItem.screenPosition,
					disconnectable: selectedSceneItem.disconnectable,
					rotatable: selectedSceneItem.rotatable,
				};
				dispatch(selectSceneItem(newSceneItem));
			} else if (selectedArmrest == armrests?.wideArmrest && armrests.regularArmrest) {
				dispatch(setSelectedArmrest(armrests.regularArmrest, true));
				dispatch(selectSceneArticle(armrests.regularArmrest.productId));
				const newSceneItem = {
					id: selectedSceneItem.id,
					articleId: armrests.regularArmrest.productId,
					screenPosition: selectedSceneItem.screenPosition,
					disconnectable: selectedSceneItem.disconnectable,
					rotatable: selectedSceneItem.rotatable,
				};
				dispatch(selectSceneItem(newSceneItem));
			}
		}
	};

	useEffect(() => {
		if (selectedCover?.coverName && armrests) {
			if (selectedArmrest && selectedArmrest.filter.color !== selectedCover.coverName) {
				const wide = selectedArmrest.modelId.includes('WIDE');
				const newArmrests = armrests[selectedCover.coverName];
				const { regularArmrest, wideArmrest } = newArmrests;
				if (newArmrests) {
					if (wide) {
						if (wideArmrest && wideArmrest !== selectedArmrest) {
							dispatch(setSelectedArmrest(wideArmrest, false));
						} else if (regularArmrest && regularArmrest !== selectedArmrest) {
							dispatch(setSelectedArmrest(regularArmrest, false));
							onClickChangeArmrest();
						}
					} else if (regularArmrest && regularArmrest !== selectedArmrest) {
						dispatch(setSelectedArmrest(newArmrests.regularArmrest, false));
					}
				}
			}
		}
	}, [selectedCover]);
	return (
		<ArmrestPopoverButton
			orientation={orientation}
			writeDirection={writeDirection}
			platform={platform}
			deviceType={deviceType}
			showInfoButton={showInfoButton}
			showChangeArmrestButton={hasArmrestPicker && selectedItemFamily === 'ARMREST' && wideArmrest}
			onClickChangeArmrest={(): void => onClickChangeArmrest()}
		></ArmrestPopoverButton>
	);
};

export default ArmrestPopoverButtonContainer;
